import React, { useEffect, useState, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  FiArrowLeft,
  FiFileText,
  FiUser,
  FiPhone,
  FiMapPin,
  FiPackage,
  FiCheckCircle,
  FiXCircle,
  FiTruck,
  FiClock,
  FiCircle,
  FiMessageSquare,
  FiSend,
  FiX,
  FiDownload,
} from "react-icons/fi";
import { useTheme } from "../../../contexts/ThemeContext";
import SellerMasterLayout from "../../../layouts/seller/SellerMasterLayout";
import AppContext from "../../../contexts/AppContext";
import "../../../assets/css/seller/subscreens/orders/orderview/OrderView.css";

const PLATFORM_COMMISSION_PERCENT = 7;

const OrderView = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const {
    baseUrl,
    baseUrlForFiles,
    sellerSelectedOrder: orderData,
    seller,
    sellerAuthToken,
  } = useContext(AppContext);

  const [status, setStatus] = useState(orderData?.status || "");

  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const invoiceRef = useRef(null);

  const fetchMessages = () => {
    axios
      .get(`${baseUrl}/order/chat/getMessages`, {
        params: { order_id: orderData?.id },
        headers: {
        Authorization: `Bearer ${sellerAuthToken}`,
      },
      })
      .then((res) => setChatMessages(res.data?.messages || []))
      .catch(() => {});
  };

  const handleSendMessage = () => {
    if (!newMessage.trim()) return;

    const formdata = new FormData();
    formdata.append("order_id", orderData.id);
    formdata.append("seller_id", seller?.id);
    formdata.append("customer_id", orderData.chcustomer_id);
    formdata.append("message", newMessage);
    formdata.append("sender", "chseller");
    setNewMessage("");

    axios
      .post(`${baseUrl}/seller/order/chat/sendMessage`, formdata, {
        headers: {
          "Content-Type": "multipart/form-data",
           Authorization: `Bearer ${sellerAuthToken}`,
        },
      })
      .then(() => fetchMessages())
      .catch(() => {});
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!orderData) navigate("/seller/orders", { replace: true });
  }, [orderData?.id]);

  if (!orderData) return null;

  const updateStatus = (newStatus) => {
    const formdata = new FormData();
    formdata.append("orderid", orderData.id);
    formdata.append("status", newStatus);
    axios
      .post(`${baseUrl}/seller/order/updateOrderStatus`, formdata, {
        headers: {
          "Content-Type": "multipart/form-data",
           Authorization: `Bearer ${sellerAuthToken}`,
        },
      },)
      .then(() => {
        setStatus(newStatus);
        toast.success("Status updated!");
      })
      .catch(() => toast.error("Failed to update. Try again."));
  };

  const getStatusClass = (s) => {
    const map = {
      Processing: "ov-status-processing",
      Shipped: "ov-status-shipped",
      completed: "ov-status-delivered",
      Cancelled: "ov-status-cancelled",
    };
    return map[s] || "";
  };

  const isLocked = status === "completed" || status === "Cancelled";

  // ── Payment summary numbers (used by both invoice and payment breakdown card) ──
  const invoiceSubtotal = (orderData.cart || []).reduce(
    (sum, item) => sum + Number(item.price) * item.quantity,
    0,
  );
  const invoiceShipping = Number(orderData.total_amount) - invoiceSubtotal;

  // ── Payment Breakdown: commission + net amount ──
  // Order completed ho chuka ho (status ke hisab se, DB ki 0/null value ke hisab se nahi)
  // to backend se aayi actual commission_amount use karo, warna abhi tak deduct nahi
  // hui isliye estimate dikhado (7% of product price).
  const getCommissionAmount = () => {
    if (status === "completed") {
      return Number(orderData.commission_amount);
    }
    return invoiceSubtotal * (PLATFORM_COMMISSION_PERCENT / 100);
  };
  const commissionAmount = getCommissionAmount();
  const netAmount = invoiceSubtotal - commissionAmount;

  // ── Invoice: download the invoice content as a real PDF file ──────
  const handleDownloadPdf = () => {
    if (!invoiceRef.current) return;
    setIsGeneratingPdf(true);

    html2canvas(invoiceRef.current, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
    })
      .then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

        pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
        pdf.save(`Invoice-Order-${orderData.id}.pdf`);
      })
      .catch(() => {
        toast.error("PDF can't be generated. Try Again.");
      })
      .finally(() => setIsGeneratingPdf(false));
  };

  return (
    <SellerMasterLayout>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className={`sd-shell ${isDark ? "dark-mode" : "light-mode"}`}>
        <div className="sor-card-shell">
          <div className="ov-content-wrapper">
            <button
              className="ov-back-btn"
              onClick={() => navigate("/seller/orders")}
            >
              <FiArrowLeft /> Back to Orders
            </button>

            <div className="ov-header">
              <div className="ov-header-left">
                <h1 className="ov-header-id">#{orderData.id}</h1>
                <div className="ov-header-meta">
                  <span className="ov-header-date">
                    {new Date(orderData.created_at).toLocaleDateString(
                      "en-PK",
                      {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      },
                    )}
                  </span>
                  <span
                    className={`ov-header-status ${getStatusClass(status)}`}
                  >
                    {status}
                  </span>
                </div>
              </div>
              <div className="ov-header-right">
                <div className="ov-header-total">
                  <span className="ov-total-label">Order Total</span>
                  <span className="ov-total-amount">
                    Rs {Number(orderData.total_amount).toLocaleString()}
                  </span>
                </div>
                <div className="ov-header-actions">
                  <button
                    className="ov-action-btn"
                    onClick={() => setShowInvoiceModal(true)}
                  >
                    <FiFileText /> Invoice
                  </button>

                </div>
              </div>
            </div>

            <div className="ov-layout">
              <div className="ov-left">
                <div className="ov-card">
                  <h3 className="ov-card-title">
                    <FiUser /> Customer Details
                  </h3>
                  <div className="ov-customer-info">
                    <div className="ov-customer-row">
                      <div className="ov-customer-icon">
                        <FiUser />
                      </div>
                      <div className="ov-customer-data">
                        <span className="ov-customer-label">Name</span>
                        <span className="ov-customer-value">
                          {orderData.full_name}
                        </span>
                      </div>
                    </div>
                    <div className="ov-customer-row">
                      <div className="ov-customer-icon">
                        <FiPhone />
                      </div>
                      <div className="ov-customer-data">
                        <span className="ov-customer-label">Phone</span>
                        <span className="ov-customer-value">
                          {orderData.phone}
                        </span>
                      </div>
                    </div>
                    <div className="ov-customer-row">
                      <div className="ov-customer-icon">
                        <FiMapPin />
                      </div>
                      <div className="ov-customer-data">
                        <span className="ov-customer-label">Address</span>
                        <span className="ov-customer-value">
                          {orderData.address}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="ov-card">
                  <h3 className="ov-card-title">
                    <FiPackage /> Order Items
                  </h3>
                  <div className="ov-products-list">
                    {(orderData.cart || []).map((item) => {
                      const imgSrc =
                        item.product?.images?.length > 0
                          ? `${baseUrlForFiles}/${item.product.images[0].image_path}`
                          : "/placeholder.png";
                      return (
                        <div key={item.id} className="ov-product-item">
                          <div
                            className={`ov-product-main ${item.isCustomized == 1 ? "ov-clickable" : ""}`}
                            onClick={() => {
                              if (item.isCustomized == 1) {
                                navigate("/seller/messages", {
                                  state: { productId: item.product?.id },
                                });
                              }
                            }}
                          >
                            <img
                              src={imgSrc}
                              alt={item.product?.product_name}
                              style={{
                                width: 50,
                                height: 50,
                                borderRadius: 8,
                                objectFit: "cover",
                                flexShrink: 0,
                              }}
                            />
                            <div className="ov-product-info">
                              <h4 className="ov-product-name">
                                {item.product?.product_name}
                                {item.isCustomized == 1 && (
                                  <span className="ov-product-custom-badge">
                                    Customized
                                  </span>
                                )}
                              </h4>
                              <div className="ov-product-meta">
                                <span>Qty: {item.quantity}</span>
                                <span>•</span>
                                <span className="ov-product-price">
                                  Rs {Number(item.price).toLocaleString()}
                                </span>
                                {item.size &&
                                  item.size !== "null" &&
                                  item.size !== "" && (
                                    <>
                                      <span>•</span>
                                      <span>Size: {item.size}</span>
                                    </>
                                  )}
                                {item.product?.variant?.colors &&
                                  item.product.variant.colors.trim() !== "" && (
                                    <>
                                      <span>•</span>
                                      <span className="ov-color-wrap">
                                        Color:
                                        <span
                                          className="ov-color-dot"
                                          style={{
                                            display: "inline-block",
                                            width: 12,
                                            height: 12,
                                            borderRadius: "50%",
                                            marginLeft: 4,
                                            verticalAlign: "middle",
                                            border: "1px solid #ccc",
                                            backgroundColor:
                                              item.product.variant.colors.split(
                                                ",",
                                              )[item.color] ?? "transparent",
                                          }}
                                        />
                                      </span>
                                    </>
                                  )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="ov-products-total">
                    <span className="ov-products-total-label">
                      Total Items:
                    </span>
                    <span className="ov-products-total-count">
                      {(orderData.cart || []).reduce(
                        (sum, i) => sum + i.quantity,
                        0,
                      )}
                    </span>
                  </div>
                </div>
              </div>

              <div className="ov-right">
                <div className="ov-card">
                  <h3 className="ov-card-title">
                    <FiCheckCircle /> Order Status Management
                  </h3>
                  <div className="ov-status-content">
                    <div className="ov-status-dropdown-wrapper">
                      <label className="ov-status-label">Current Status</label>
                      <select
                        className="ov-status-dropdown"
                        value={status}
                        onChange={(e) => updateStatus(e.target.value)}
                        disabled={isLocked}
                      >
                        <option value="Processing">Processing</option>
                        <option value="Shipped">Shipped</option>
                        <option value="completed">Completed</option>
                        <option value="Cancelled">Cancelled</option>
                      </select>
                    </div>

                    {status === "Processing" && (
                      <div className="ov-status-actions">
                        <div className="ov-status-buttons">
                          <button
                            className="ov-status-btn ov-status-ship"
                            onClick={() => updateStatus("Shipped")}
                          >
                            <FiTruck /> Mark as Shipped
                          </button>
                          <button
                            className="ov-status-btn ov-status-reject"
                            onClick={() => updateStatus("Cancelled")}
                          >
                            <FiXCircle /> Cancel Order
                          </button>
                        </div>
                      </div>
                    )}

                    {status === "Shipped" && (
                      <div className="ov-status-actions">
                        <button
                          className="ov-status-btn ov-status-deliver"
                          onClick={() => updateStatus("completed")}
                        >
                          <FiCheckCircle /> Mark as Completed
                        </button>
                      </div>
                    )}

                    {isLocked && (
                      <div className="ov-status-locked">
                        <FiCheckCircle />
                        <span>Status is locked and cannot be changed</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="ov-card">
                  <h3 className="ov-card-title">
                    <FiClock /> Order Timeline
                  </h3>
                  <div className="ov-timeline-list">
                    {[
                      { label: "Order Placed", done: true },
                      { label: "Processing", done: status !== "Cancelled" },
                      {
                        label: "Shipped",
                        done: status === "Shipped" || status === "completed",
                      },
                      { label: "Completed", done: status === "completed" },
                    ].map((item, index) => (
                      <div
                        key={index}
                        className={`ov-timeline-item ${item.done ? "completed" : "pending"}`}
                      >
                        <div className="ov-timeline-icon">
                          {item.done ? <FiCheckCircle /> : <FiCircle />}
                        </div>
                        <div className="ov-timeline-content">
                          <span className="ov-timeline-status">
                            {item.label}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Payment Breakdown: full-width card, sabse last mein ── */}
            <div className="ov-card ov-payment-fullwidth">
              <h3 className="ov-card-title">
                <span className="ov-rs-badge">Rs</span> Payment Breakdown
              </h3>
              <div className="ov-payment-breakdown">
                <div className="ov-payment-row">
                  <span>Product Price</span>
                  <span>Rs {invoiceSubtotal.toLocaleString()}</span>
                </div>
                <div className="ov-payment-row">
                  <span>Delivery Charges</span>
                  <span>Rs {invoiceShipping.toLocaleString()}</span>
                </div>
                <div className="ov-payment-row ov-payment-row-total">
                  <span>Order Total</span>
                  <span>
                    Rs {Number(orderData.total_amount).toLocaleString()}
                  </span>
                </div>
                <div className="ov-payment-row ov-payment-row-commission">
                  <span>
                    Platform Commission ({PLATFORM_COMMISSION_PERCENT}%)
                  </span>
                  <span>- Rs {commissionAmount.toLocaleString()}</span>
                </div>
                {status === "completed" ? (
                  <p className="ov-payment-note">
                    This amount has been deducted from your wallet balance for
                    this order.
                  </p>
                ) : (
                  <p className="ov-payment-note">
                    This amount will be deducted from your wallet balance once
                    the order is marked Completed.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── INVOICE MODAL ── */}
      {showInvoiceModal && (
        <div
          className="ov-invoice-overlay"
          onClick={() => setShowInvoiceModal(false)}
        >
          <div
            className="ov-invoice-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="ov-invoice-close"
              onClick={() => setShowInvoiceModal(false)}
            >
              <FiX />
            </button>

            {/* Content that gets captured into the PDF */}
            <div className="ov-invoice-content" ref={invoiceRef}>
              <div className="ov-invoice-top">
                <div>
                  <h2 className="ov-invoice-brand">CraftHive</h2>
                </div>
                <div className="ov-invoice-meta">
                  <p>
                    <strong>Invoice #</strong> {orderData.id}
                  </p>
                  <p>
                    <strong>Date:</strong>{" "}
                    {new Date(orderData.created_at).toLocaleDateString(
                      "en-PK",
                      {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      },
                    )}
                  </p>
                </div>
              </div>

              <hr className="ov-invoice-divider" />

              <div className="ov-invoice-parties">
                <div className="ov-invoice-billfrom">
                  <h4>Bill From</h4>
                  <p>{seller?.store_name}</p>
                  <p>{seller?.phone}</p>
                  <p>
                    {seller?.business_address}
                    {seller?.city ? `, ${seller.city}` : ""}
                  </p>
                </div>
                <div className="ov-invoice-billto">
                  <h4>Bill To</h4>
                  <p>{orderData.full_name}</p>
                  <p>{orderData.phone}</p>
                  <p>{orderData.address}</p>
                </div>
              </div>

              <div className="ov-invoice-payment-summary">
                <h4>Payment Summary</h4>
                <div className="ov-invoice-summary-row ov-invoice-summary-grand">
                  <span>Total Amount</span>
                  <span>
                    Rs {Number(orderData.total_amount).toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="ov-invoice-note">
                <p>
                  <strong>Note:</strong> Thank you for shopping with CraftHive!

                  This is a system-generated invoice for your order. For any

                  queries regarding this order, please reach out via help and support

                </p>
              </div>
            </div>

            <button
              className="ov-invoice-download-btn"
              onClick={handleDownloadPdf}
              disabled={isGeneratingPdf}
            >
              <FiDownload /> {isGeneratingPdf ? "Generating..." : "Save as PDF"}
            </button>
          </div>
        </div>
      )}
    </SellerMasterLayout>
  );
};

export default OrderView;